import { useCallback, useMemo, useState } from "react"

// A minimal, per-browser favorite store — no backend model exists yet for
// favoriting files/folders/albums. This is intentionally the smallest thing
// that works: it does not sync across devices or users, but it lets a user
// mark and find their own favorites within this browser, matching Phase 3's
// "minimum functionality necessary" instruction rather than waiting for a
// full backend feature.
const storageKey = (projectId: string) => `files-favorites:${projectId}`

const readStored = (projectId: string): Set<string> => {
  try {
    const raw = window.localStorage.getItem(storageKey(projectId))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? new Set(parsed) : new Set()
  } catch {
    return new Set()
  }
}

export function useFavorites(projectId: string) {
  const [favoriteKeys, setFavoriteKeys] = useState<Set<string>>(() =>
    readStored(projectId)
  )

  const persist = useCallback(
    (next: Set<string>) => {
      setFavoriteKeys(next)
      try {
        window.localStorage.setItem(
          storageKey(projectId),
          JSON.stringify([...next])
        )
      } catch {
        // Storage can be unavailable (private mode, quota) — the in-memory
        // state still works for the rest of this session.
      }
    },
    [projectId]
  )

  const toggleFavorite = useCallback(
    (key: string) => {
      const next = new Set(favoriteKeys)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      persist(next)
    },
    [favoriteKeys, persist]
  )

  const isFavorite = useCallback(
    (key: string) => favoriteKeys.has(key),
    [favoriteKeys]
  )

  return useMemo(
    () => ({ favoriteKeys, isFavorite, toggleFavorite }),
    [favoriteKeys, isFavorite, toggleFavorite]
  )
}
